import {
  IDefaultDimension,
  IReportDatasetLens,
  IReportDatasetLensesResponse,
  IReportDimension,
  IReportDimensionMetadataResponse
} from '@opengov/reporting-types';
import { getServiceURL } from './transportWrapper';
import { IDataset, IRequestOptions, IStandardDataset } from './types';
import NodeCache from 'node-cache';

const delphiusHost = getServiceURL(process.env.DELPHIUS_DEFAULT_HOST);
const delphiusDatasetsHost = getServiceURL(process.env.DELPHIUS_DATASETS_HOST);

const getDatasetServiceUrl = (serviceHost?: string) => getServiceURL(serviceHost || process.env.DATASET_SERVICE_HOST);

export const cache = new NodeCache({ stdTTL: Number(process.env.CACHE_TTL) || 60 * 10 });

export const datasetById = async (
  entityId: string | number,
  datasetId: string | number,
  clientOptions?: IRequestOptions
): Promise<IDataset> =>
  delphiusDatasetsHost
    .options(clientOptions, true)
    .url(`/api/grid_data/v3/transaction_datasets/${datasetId}?entity_id=${entityId}&embed=status`)
    .get()
    .json()
    .then((dataset: IDataset): IDataset => {
      return dataset;
    });

export const datasetByType = async (
  entityId: string | number,
  datasetType: string,
  limit: number,
  clientOptions?: IRequestOptions
): Promise<IDataset[]> =>
  delphiusHost
    .options(clientOptions, true)
    .url(`/api/v1/data_sets?entity_id=${entityId}&data_set_type=${datasetType}&limit=${limit}`)
    .get()
    .json()
    .then((datasets: IDataset[]): IDataset[] => {
      return datasets;
    });

export const actualsDatasets = async (entityId: string, limit: number, clientOptions?: IRequestOptions) =>
  delphiusHost
    .options(clientOptions, true)
    .url(`/api/v1/data_sets?entity_id=${entityId}&data_set_type=actual&limit=${limit}`)
    .get()
    .json()
    .then((datasets: IDataset[]): IDataset[] => {
      return datasets;
    });

export const getSchema = async <T>(urn: string, clientOptions?: RequestInit, hostUrl?: string): Promise<T> => {
  // if we remove caching for getSchema we will also miss out on many get dimension calls.
  // as a result of which caching will not be useful
  // so we can only disable caching for lens
  if (urn.startsWith('lens')) {
    return getDatasetServiceUrl(hostUrl).options(clientOptions, true).url(`/api/datasets/v1/${urn}`).get().json<T>();
  }

  if (cache.has(urn)) {
    return cache.get(urn);
  }
  return getDatasetServiceUrl(hostUrl)
    .options(clientOptions, true)
    .url(`/api/datasets/v1/${urn}`)
    .get()
    .json<T>()
    .then((data) => {
      cache.set(urn, data);
      return data;
    });
};

/** @deprecated use getSchema */
export const getLens = (...args: Parameters<typeof getSchema>) => getSchema<IReportDatasetLens>(...args);
export type GetLensFn = typeof getLens;

/** @deprecated use getSchema */
export const getDimension = (...args: Parameters<typeof getSchema>) => getSchema<IReportDimension>(...args);
export type GetDimensionFn = typeof getDimension;

export const getLenses = async (
  params: { entityUuid: string; datasetId?: string; pageSize?: number; type?: string | string[]; cursor?: string },
  clientOptions?: IRequestOptions,
  hostUrl?: string
): Promise<IReportDatasetLensesResponse> => {
  let req = getDatasetServiceUrl(hostUrl)
    .options(clientOptions, true)
    .url('/api/datasets/v1/lenses')
    .query({
      entity_id: params.entityUuid,
      ...(params.datasetId && { source_id: params.datasetId }),
      page_size: params.pageSize || 100,
      ...(params.cursor && { cursor: params.cursor })
    });

  if (params.type) {
    const types = Array.isArray(params.type) ? params.type : [params.type];
    req = req.query({ type: types });
  }

  return req
    .get()
    .json()
    .then((response: IReportDatasetLensesResponse): IReportDatasetLensesResponse => response);
};

export const getDimensionMetadata = async (dimensionUrn: string, clientOptions?: IRequestOptions, hostUrl?: string) => {
  if (clientOptions && clientOptions.isCachingAllowed) {
    if (cache.has(`${dimensionUrn}-metadata`)) {
      return cache.get(`${dimensionUrn}-metadata`) as IReportDimensionMetadataResponse;
    }

    return getDatasetServiceUrl(hostUrl)
      .options(clientOptions, true)
      .url(`/api/datasets/v1/resources/stdc-metadata/content/${dimensionUrn}`)
      .get()
      .json()
      .then((dimensionMetadata: IReportDimensionMetadataResponse): IReportDimensionMetadataResponse => {
        cache.set(`${dimensionUrn}-metadata`, dimensionMetadata);
        return dimensionMetadata;
      });
  } else {
    return getDatasetServiceUrl(hostUrl)
      .options(clientOptions, true)
      .url(`/api/datasets/v1/resources/stdc-metadata/content/${dimensionUrn}`)
      .get()
      .json()
      .then((dimensionMetadata: IReportDimensionMetadataResponse): IReportDimensionMetadataResponse => {
        return dimensionMetadata;
      });
  }
};

export type GetDimensionMetadataFn = typeof getDimensionMetadata;

export const getDefaultDimensions = async (
  params: { entityUuid: string },
  clientOptions?: IRequestOptions,
  hostUrl?: string
) => {
  if (clientOptions && clientOptions.isCachingAllowed) {
    if (cache.has(params.entityUuid)) {
      return cache.get(params.entityUuid) as IDefaultDimension[];
    }

    return getDatasetServiceUrl(hostUrl)
      .options(clientOptions, true)
      .url(`/api/datasets/v1/dimensions/default`)
      .query({ entity_id: params.entityUuid })
      .get()
      .json()
      .then((data: { defaultDimensions: IDefaultDimension[] }) => {
        cache.set(params.entityUuid, data.defaultDimensions);
        return data.defaultDimensions;
      });
  } else {
    return getDatasetServiceUrl(hostUrl)
      .options(clientOptions, true)
      .url(`/api/datasets/v1/dimensions/default`)
      .query({ entity_id: params.entityUuid })
      .get()
      .json()
      .then((data: { defaultDimensions: IDefaultDimension[] }) => data.defaultDimensions);
  }
};

export type GetDefaultDimensionsFn = typeof getDefaultDimensions;

export const createStandardDataset = async (dataset: IStandardDataset, clientOptions?: IRequestOptions) =>
  getDatasetServiceUrl()
    .options(clientOptions, true)
    .url('/api/datasets/v1/datasets/standard')
    .json({
      name: dataset.name,
      description: dataset.description,
      entityId: dataset.entityId,
      extendedSchemaColumns: dataset.extendedSchemaColumns,
      isHidden: dataset.isHidden,
      type: dataset.type,
      managed: { type: dataset.managed.type }
    })
    .post()
    .json()
    .then((result) => result);

export const deleteStandardDataset = async (id: string | number, clientOptions: RequestInit = {}, hostUrl?: string) =>
  getDatasetServiceUrl(hostUrl)
    .options(clientOptions, true)
    .url(`/api/datasets/v1/dataset/entity/beginning-fund-balance/${id}`)
    .delete();

export const getDatasetUploads = async (entitySubdomain: string, datasetId: string, clientOptions?: IRequestOptions) =>
  delphiusDatasetsHost
    .options(clientOptions, true)
    .url(`/api/grid_data/v3/uploads`)
    .query({ entity_id: entitySubdomain, dataset_id: datasetId })
    .get()
    .json()
    .then((result) => result);

export interface IDimensionItem {
  id: string;
  name: string;
  standardType?: string;
  managed?: { type: string; application: string; sourceId: string };
}

export interface IDimensionsListResponse {
  entityId: string;
  dimensions: IDimensionItem[];
}

export const getDimensionsByManagedSourceId = async (
  managedSourceId: string,
  clientOptions?: IRequestOptions,
  hostUrl?: string
): Promise<IDimensionsListResponse> =>
  getDatasetServiceUrl(hostUrl)
    .options(clientOptions, true)
    .url('/api/datasets/v1/dimensions')
    .query({ managed_source_id: managedSourceId })
    .get()
    .json<IDimensionsListResponse>();

export type GetDimensionsByManagedSourceIdFn = typeof getDimensionsByManagedSourceId;
